import './style.scss';

function addMediaQueryListener(mediaQuery, handler) {
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handler);
    return;
  }

  if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(handler);
  }
}

function hydratePoster(video, state) {
  if (state.posterLoaded) {
    return;
  }

  const poster = video.dataset.poster;
  if (poster) {
    video.poster = poster;
    video.removeAttribute('data-poster');
  }

  state.posterLoaded = true;
}

function hydrateVideoSources(video, state) {
  if (state.sourcesLoaded) {
    return;
  }

  hydratePoster(video, state);

  const sources = Array.from(video.querySelectorAll('source[data-src]'));
  sources.forEach((source) => {
    if (!source.dataset.src) {
      return;
    }

    source.src = source.dataset.src;
    source.removeAttribute('data-src');
  });

  state.sourcesLoaded = true;
  video.load();
}

function initProjectGallery(block) {
  if (block.dataset.projectGalleryInitialized === 'true') {
    return;
  }

  block.dataset.projectGalleryInitialized = 'true';

  const videos = Array.from(block.querySelectorAll('[data-project-gallery-video]'));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 767px)');
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);

  // Images are handled by the theme-wide progressive image loader.
  if (!videos.length) {
    return;
  }

  const states = new Map();
  videos.forEach((video) => {
    video.muted = true;
    video.defaultMuted = true;
    states.set(video, {
      posterLoaded: false,
      sourcesLoaded: false,
      visible: false,
    });
  });

  const prepareVideo = (video) => {
    const state = states.get(video);
    if (!state) {
      return;
    }

    // Respect data-saver and reduced-motion by loading only the static poster.
    if (saveData || reducedMotion.matches) {
      hydratePoster(video, state);
      return;
    }

    hydrateVideoSources(video, state);
  };

  const playVideo = (video) => {
    const state = states.get(video);
    if (!state || !state.visible || document.hidden) {
      return;
    }

    if (saveData || reducedMotion.matches) {
      hydratePoster(video, state);
      video.pause();
      return;
    }

    hydrateVideoSources(video, state);
    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const setVisibility = (video, visible) => {
    const state = states.get(video);
    if (!state) {
      return;
    }

    state.visible = visible;
    if (visible) {
      playVideo(video);
    } else {
      video.pause();
    }
  };

  if ('IntersectionObserver' in window) {
    const preloadMargin = saveData
      ? '0px'
      : (mobileViewport.matches ? '320px 0px' : '600px 0px');

    const preloadObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        prepareVideo(entry.target);
        preloadObserver.unobserve(entry.target);
      });
    }, {
      rootMargin: preloadMargin,
      threshold: 0,
    });

    const visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setVisibility(entry.target, entry.isIntersecting);
      });
    }, {
      rootMargin: '0px',
      threshold: 0.01,
    });

    videos.forEach((video) => {
      preloadObserver.observe(video);
      visibilityObserver.observe(video);
    });
  } else {
    const checkVideos = () => {
      const preloadDistance = saveData ? 0 : (mobileViewport.matches ? 320 : 600);

      videos.forEach((video) => {
        const rect = video.getBoundingClientRect();
        const visible = rect.bottom > 0 && rect.top < window.innerHeight;
        const nearViewport = rect.bottom > -preloadDistance && rect.top < window.innerHeight + preloadDistance;

        if (nearViewport) {
          prepareVideo(video);
        }
        setVisibility(video, visible);
      });
    };

    checkVideos();
    window.addEventListener('scroll', checkVideos, {passive: true});
    window.addEventListener('resize', checkVideos, {passive: true});
  }

  document.addEventListener('visibilitychange', () => {
    videos.forEach((video) => {
      const state = states.get(video);
      if (!state) {
        return;
      }

      if (document.hidden) {
        video.pause();
      } else if (state.visible) {
        playVideo(video);
      }
    });
  });

  addMediaQueryListener(reducedMotion, () => {
    videos.forEach((video) => {
      const state = states.get(video);
      if (!state) {
        return;
      }

      if (reducedMotion.matches) {
        hydratePoster(video, state);
        video.pause();
      } else if (state.visible) {
        playVideo(video);
      }
    });
  });
}

export function project_gallery(root = document) {
  const selector = '[data-component="project-gallery"]';
  const context = root || document;
  const blocks = [];

  if (context.nodeType === 1 && context.matches(selector)) {
    blocks.push(context);
  }

  if (context.querySelectorAll) {
    blocks.push(...context.querySelectorAll(selector));
  }

  blocks.forEach(initProjectGallery);
}
